import React, { useMemo, useEffect } from 'react'
import { groupBy, uniq } from 'lodash'
import styled from '@emotion/styled'
import { css } from '@emotion/core'
import { FormattedMessage } from 'react-intl'

// components
import TriangleCompetency from 'components/ui/icons/TriangleCompetency'
import { ParagraphSmallCaps, Paragraph1 } from 'components/Paragraphs'
import { SectionTitle, SectionTitleContainer } from 'components/Panel/common'

import Line from 'components/Panel/TreeList/Line'

// data
import concepts from 'data/output/concept-nodes.json'
import conceptsToCompetencies from 'data/output/concept-to-competencies-dict.json'
import edges from 'data/output/concept-deduped-links.json'
import conceptSourceToLinkIndex from 'data/output/concept-source-to-links-index.json'
import conceptTargetToLinkIndex from 'data/output/concept-target-to-links-index.json'

// accessors
import { useSelection } from 'flow/interactions/accessors'
import { nodeIdToIndex } from 'flow/queryparams'

const TriangleCompetencyStyled = styled(TriangleCompetency)`
  margin-left: 10px;
`

const ListContainer = styled.div`
  padding-bottom: 70px;
`

const CategoryLineText = styled(ParagraphSmallCaps)(({ theme }) => css`
  text-transform: uppercase;
  opacity: ${theme.labelOpacity};
  border-bottom: 1px solid ${theme.separator};
  padding: 14px 0 12px 0;
`)

const LineText = styled(Paragraph1)(({ theme }) => css`
  padding: 8px 0 6px 10px;
  border-bottom: 1px solid ${theme.separator};
`)

const TreeListConcepts = ({ currentNode, nodes, selectedId, setSelectedId }) => {
  const [selection, setSelection] = useSelection()

  useEffect(() => {
    return () => {
      // Reset when the node is changing
      setSelection([])
    }
  }, [currentNode.data.id, setSelection])

  // competencies
  const competencies = useMemo(() => {
    const groups = groupBy(
      (conceptsToCompetencies[currentNode.data.id] || [])
        .map((id) => concepts[nodeIdToIndex[id]]),
      'skill'
    )
    return Object.keys(groups)
      .sort((a, b) => {
        return a.localeCompare(b, undefined, {
          numeric: true,
          sensitivity: 'base'
        })
      })
      .reduce((p, c) => {
        p[c] = groups[c]
        return p
      }, {})
  }, [currentNode])

  // types
  const types = useMemo(() => {
    const usableNodes = nodes || []
    const typesAll = {}
    const outgoingRelations = (conceptSourceToLinkIndex[currentNode.data.id] || [])
    const incomingRelations = (conceptTargetToLinkIndex[currentNode.data.id] || [])
    const relations = uniq([...outgoingRelations, ...incomingRelations])

    relations.forEach((relationIndex) => {
      const edge = edges[relationIndex]
      Object.keys(edge.values)
        .forEach((categoryKey) => {
          Object.keys(edge.values[categoryKey])
            .forEach((typeKey) => {
              if (typeKey !== 'competency') {
                typesAll[typeKey] = typesAll[typeKey] || []
                const connectedNode = currentNode.data.id === edge.target ? edge.source : edge.target
                if (!typesAll[typeKey].includes(connectedNode)) {
                  typesAll[typeKey].push(connectedNode)
                }
              }
            })
        })
    })

    return Object.keys(typesAll)
      .sort((a, b) => {
        return a.localeCompare(b, undefined, {
          numeric: true,
          sensitivity: 'base'
        })
      })
      .reduce((p, c) => {
        p[c] = typesAll[c]
          .map((id) => usableNodes.find(n => n.data.id === id))
          .sort((a, b) => {
            return a.data.label.localeCompare(b.data.label, undefined, {
              numeric: true,
              sensitivity: 'base'
            })
          })
        return p
      }, {})
  }, [nodes, currentNode])

  return (
    <>
      <SectionTitleContainer>
        <SectionTitle opacify vAlignCenter>
          <FormattedMessage id='list-title-concept-1' />
          <TriangleCompetencyStyled />
        </SectionTitle>
      </SectionTitleContainer>
      {competencies && (
        <ListContainer>
          {
            // competencies
            !Object.keys(competencies).length
              ? (
                <Paragraph1>
                  <FormattedMessage id='liste-node-no-competency' />
                </Paragraph1>
              )
              : Object
                .keys(competencies).map(skill => {
                  return (
                    <div key={skill}>
                      <CategoryLineText>
                        {skill}
                      </CategoryLineText>
                      {
                        competencies[skill].map(competency => (
                          <LineText key={competency?.id}>
                            {competency?.label}
                          </LineText>
                        ))
                      }
                    </div>
                  )
                })
          }
        </ListContainer>
      )}

      <SectionTitleContainer>
        <SectionTitle opacify>
          <FormattedMessage id='list-title-concept-2' />
        </SectionTitle>
      </SectionTitleContainer>
      {
        (types && nodes) && (
          <ListContainer>
            {
              // types
              !Object.keys(types).length
                ? (
                  <Paragraph1>
                    <FormattedMessage id='liste-node-no-concept' />
                  </Paragraph1>
                )
                : Object.keys(types)
                  .map(d => (
                    <div key={d}>
                      <CategoryLineText>
                        {d}
                      </CategoryLineText>
                      {types[d].map(concept => {
                        return (
                          <Line
                            key={concept?.data.id}
                            data={concept?.data}
                            setSelection={setSelection}
                            selection={selection}
                          />
                        )
                      })}
                    </div>
                  ))
            }
          </ListContainer>
        )
      }
    </>
  )
}

export default TreeListConcepts
